import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  FileText, FolderOpen, Shield, AlertTriangle, Plus, RefreshCw, Eye, CheckCircle, Clock, Download, Trash2, BookOpen,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DocumentsPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("documents");
  const [loading, setLoading] = useState(false);
  
  // Data
  const [documents, setDocuments] = useState([]);
  const [expiringDocs, setExpiringDocs] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [complianceDashboard, setComplianceDashboard] = useState(null);
  const [employees, setEmployees] = useState([]);
  
  // Filters
  const [selectedKategori, setSelectedKategori] = useState("all");
  const [selectedEmployee, setSelectedEmployee] = useState("all");
  
  // Dialogs
  const [showDocDialog, setShowDocDialog] = useState(false);
  const [showPolicyDialog, setShowPolicyDialog] = useState(false);
  const [showPolicyDetail, setShowPolicyDetail] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  
  // Forms
  const [docForm, setDocForm] = useState({
    nama: "", kategori: "dokumen", deskripsi: "", file_url: "",
    employee_id: "", tanggal_berlaku: "", tanggal_kadaluarsa: "", is_confidential: false
  });
  const [policyForm, setPolicyForm] = useState({
    judul: "", kategori: "hr", konten: "", versi: "1.0", berlaku_sejak: ""
  });

  useEffect(() => {
    fetchEmployees();
    fetchComplianceDashboard();
  }, []);

  useEffect(() => {
    if (activeTab === "documents") {
      fetchDocuments();
      fetchExpiringDocs();
    } else if (activeTab === "policies") {
      fetchPolicies();
    }
  }, [activeTab, selectedKategori, selectedEmployee]);

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem("silsa_token");
      const res = await axios.get(`${API_URL}/api/employees`, { headers: { Authorization: `Bearer ${token}` } });
      setEmployees(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("silsa_token");
      let url = `${API_URL}/api/documents`;
      const params = [];
      if (selectedKategori !== "all") params.push(`kategori=${selectedKategori}`);
      if (selectedEmployee !== "all") params.push(`employee_id=${selectedEmployee}`);
      if (params.length > 0) url += `?${params.join("&")}`;
      const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setDocuments(res.data);
    } catch (err) { toast.error("Gagal memuat dokumen"); }
    finally { setLoading(false); }
  };

  const fetchExpiringDocs = async () => {
    try {
      const token = localStorage.getItem("silsa_token");
      const res = await axios.get(`${API_URL}/api/documents/expiring?days=30`, { headers: { Authorization: `Bearer ${token}` } });
      setExpiringDocs(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchPolicies = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("silsa_token");
      const res = await axios.get(`${API_URL}/api/policies`, { headers: { Authorization: `Bearer ${token}` } });
      setPolicies(res.data);
    } catch (err) { toast.error("Gagal memuat kebijakan"); }
    finally { setLoading(false); }
  };

  const fetchComplianceDashboard = async () => {
    try {
      const token = localStorage.getItem("silsa_token");
      const res = await axios.get(`${API_URL}/api/compliance/dashboard`, { headers: { Authorization: `Bearer ${token}` } });
      setComplianceDashboard(res.data);
    } catch (err) { console.error(err); }
  };

  const handleCreateDocument = async () => {
    if (!docForm.nama || !docForm.file_url) {
      toast.error("Nama dan URL file wajib diisi");
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem("silsa_token");
      await axios.post(`${API_URL}/api/documents`, {
        ...docForm,
        employee_id: docForm.employee_id || null,
        tanggal_berlaku: docForm.tanggal_berlaku || null,
        tanggal_kadaluarsa: docForm.tanggal_kadaluarsa || null
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Dokumen berhasil ditambahkan");
      setShowDocDialog(false);
      setDocForm({ nama: "", kategori: "dokumen", deskripsi: "", file_url: "", employee_id: "", tanggal_berlaku: "", tanggal_kadaluarsa: "", is_confidential: false });
      fetchDocuments();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal menambah dokumen"); }
    finally { setLoading(false); }
  };

  const handleDeleteDocument = async (docId) => {
    try {
      const token = localStorage.getItem("silsa_token");
      await axios.delete(`${API_URL}/api/documents/${docId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Dokumen diarsipkan");
      fetchDocuments();
    } catch (err) { toast.error("Gagal menghapus dokumen"); }
  };

  const handleCreatePolicy = async () => {
    if (!policyForm.judul || !policyForm.konten) {
      toast.error("Judul dan konten wajib diisi");
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem("silsa_token");
      await axios.post(`${API_URL}/api/policies`, policyForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Kebijakan berhasil dibuat");
      setShowPolicyDialog(false);
      setPolicyForm({ judul: "", kategori: "hr", konten: "", versi: "1.0", berlaku_sejak: "" });
      fetchPolicies();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal membuat kebijakan"); }
    finally { setLoading(false); }
  };

  const handlePublishPolicy = async (policyId) => {
    try {
      const token = localStorage.getItem("silsa_token");
      await axios.put(`${API_URL}/api/policies/${policyId}/publish`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Kebijakan dipublikasi");
      fetchPolicies();
    } catch (err) { toast.error("Gagal mempublikasi"); }
  };

  const handleAcknowledgePolicy = async (policyId) => {
    try {
      const token = localStorage.getItem("silsa_token");
      await axios.post(`${API_URL}/api/policies/${policyId}/acknowledge`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Kebijakan telah diakui");
      fetchPolicies();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal"); }
  };

  const getDocStatusBadge = (status) => {
    switch(status) {
      case "active": return <Badge className="bg-green-500">Active</Badge>;
      case "expired": return <Badge className="bg-red-500">Expired</Badge>;
      case "archived": return <Badge variant="secondary">Archived</Badge>;
      default: return <Badge>{status}</Badge>;
    }
  };

  const getPolicyStatusBadge = (status) => {
    switch(status) {
      case "draft": return <Badge variant="secondary"><Clock className="w-3 h-3 mr-1" />Draft</Badge>;
      case "published": return <Badge className="bg-green-500"><CheckCircle className="w-3 h-3 mr-1" />Published</Badge>;
      case "archived": return <Badge variant="outline">Archived</Badge>;
      default: return <Badge>{status}</Badge>;
    }
  };

  const getKategoriBadge = (kategori) => {
    const colors = {
      kontrak: "bg-blue-500", sertifikat: "bg-green-500", izin: "bg-yellow-500",
      kebijakan: "bg-purple-500", hr: "bg-pink-500", keuangan: "bg-emerald-500",
      operasional: "bg-orange-500", keamanan: "bg-red-500"
    };
    return <Badge className={colors[kategori] || "bg-slate-500"}>{kategori}</Badge>;
  };

  return (
    <div className="p-6 space-y-6" data-testid="documents-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="page-title">Document & Compliance</h1>
          <p className="text-slate-600">Kelola dokumen dan kebijakan perusahaan</p>
        </div>
      </div>

      {/* Compliance Dashboard Summary */}
      {complianceDashboard && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Dokumen Aktif</p>
                  <p className="text-2xl font-bold">{complianceDashboard.documents.total_active}</p>
                </div>
                <FileText className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card className={complianceDashboard.documents.expiring_30_days > 0 ? "border-yellow-500" : ""}>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Expiring (30 hari)</p>
                  <p className="text-2xl font-bold text-yellow-600">{complianceDashboard.documents.expiring_30_days}</p>
                </div>
                <AlertTriangle className="w-8 h-8 text-yellow-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Kebijakan Published</p>
                  <p className="text-2xl font-bold">{complianceDashboard.policies.published}</p>
                </div>
                <BookOpen className="w-8 h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Acknowledgement Rate</p>
                  <p className="text-2xl font-bold">{complianceDashboard.policies.acknowledgement_rate}%</p>
                </div>
                <Shield className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="documents" data-testid="tab-documents"><FileText className="w-4 h-4 mr-2" />Dokumen</TabsTrigger>
          <TabsTrigger value="policies" data-testid="tab-policies"><BookOpen className="w-4 h-4 mr-2" />Kebijakan</TabsTrigger>
        </TabsList>

        {/* Documents Tab */}
        <TabsContent value="documents" className="space-y-4">
          {/* Expiring Documents Alert */}
          {expiringDocs.length > 0 && (
            <Card className="border-yellow-500 bg-yellow-50">
              <CardHeader className="pb-2">
                <CardTitle className="text-yellow-700 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  Dokumen Akan Kadaluarsa
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {expiringDocs.slice(0, 5).map((doc) => (
                    <div key={doc.id} className="flex justify-between items-center text-sm">
                      <span><strong>{doc.nama}</strong> - {doc.employee_nama}</span>
                      <Badge variant="outline" className="text-yellow-700">{doc.days_until_expiry} hari lagi</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent className="pt-4">
              <div className="flex flex-wrap gap-4 items-center justify-between">
                <div className="flex gap-4">
                  <div className="w-40">
                    <Label>Kategori</Label>
                    <Select value={selectedKategori} onValueChange={setSelectedKategori}>
                      <SelectTrigger><SelectValue placeholder="Semua" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Semua Kategori</SelectItem>
                        <SelectItem value="kontrak">Kontrak</SelectItem>
                        <SelectItem value="sertifikat">Sertifikat</SelectItem>
                        <SelectItem value="izin">Izin</SelectItem>
                        <SelectItem value="kebijakan">Kebijakan</SelectItem>
                        <SelectItem value="lainnya">Lainnya</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-48">
                    <Label>Karyawan</Label>
                    <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                      <SelectTrigger><SelectValue placeholder="Semua" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Semua / Perusahaan</SelectItem>
                        {employees.map((e) => (<SelectItem key={e.id} value={e.id}>{e.nama_lengkap}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button onClick={() => setShowDocDialog(true)} data-testid="btn-add-doc">
                  <Plus className="w-4 h-4 mr-2" />Tambah Dokumen
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Daftar Dokumen</CardTitle></CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8"><RefreshCw className="w-8 h-8 animate-spin text-slate-400" /></div>
              ) : documents.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <FolderOpen className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>Belum ada dokumen</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nama</TableHead>
                      <TableHead>Kategori</TableHead>
                      <TableHead>Pemilik</TableHead>
                      <TableHead>Berlaku</TableHead>
                      <TableHead>Kadaluarsa</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Aksi</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {documents.map((doc) => (
                      <TableRow key={doc.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-slate-400" />
                            <span className="font-medium">{doc.nama}</span>
                            {doc.is_confidential && <Badge variant="destructive" className="text-xs">Confidential</Badge>}
                          </div>
                        </TableCell>
                        <TableCell>{getKategoriBadge(doc.kategori)}</TableCell>
                        <TableCell>{doc.employee_nama || "Perusahaan"}</TableCell>
                        <TableCell>{doc.tanggal_berlaku || "-"}</TableCell>
                        <TableCell>{doc.tanggal_kadaluarsa || "-"}</TableCell>
                        <TableCell>{getDocStatusBadge(doc.status)}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" asChild>
                              <a href={doc.file_url} target="_blank" rel="noopener noreferrer"><Download className="w-4 h-4" /></a>
                            </Button>
                            {user?.role === 'super_admin' && (
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteDocument(doc.id)}>
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Policies Tab */}
        <TabsContent value="policies" className="space-y-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex justify-between">
                <Button variant="outline" onClick={fetchPolicies}><RefreshCw className="w-4 h-4 mr-2" />Refresh</Button>
                {(user?.role === 'super_admin' || user?.role === 'hr') && (
                  <Button onClick={() => setShowPolicyDialog(true)} data-testid="btn-add-policy">
                    <Plus className="w-4 h-4 mr-2" />Tambah Kebijakan
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {policies.map((policy) => (
              <Card key={policy.id} className="border hover:shadow-md transition-shadow">
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start">
                    <CardTitle className="text-lg">{policy.judul}</CardTitle>
                    {getPolicyStatusBadge(policy.status)}
                  </div>
                  <CardDescription className="flex items-center gap-2">
                    {getKategoriBadge(policy.kategori)}
                    <span className="text-xs">v{policy.versi}</span>
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-600 line-clamp-2">{policy.konten.substring(0, 100)}...</p>
                  <div className="flex justify-between items-center mt-4 text-xs text-slate-500">
                    <span>Berlaku: {policy.berlaku_sejak}</span>
                    <span>{policy.acknowledgements} acknowledgements</span>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <Button size="sm" variant="outline" onClick={() => { setSelectedPolicy(policy); setShowPolicyDetail(true); }}>
                      <Eye className="w-4 h-4 mr-1" />Baca
                    </Button>
                    {policy.status === "draft" && (user?.role === 'super_admin' || user?.role === 'hr') && (
                      <Button size="sm" onClick={() => handlePublishPolicy(policy.id)}>Publish</Button>
                    )}
                    {policy.status === "published" && (
                      <Button size="sm" variant="outline" onClick={() => handleAcknowledgePolicy(policy.id)}>
                        <CheckCircle className="w-4 h-4 mr-1" />Acknowledge
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Add Document Dialog */}
      <Dialog open={showDocDialog} onOpenChange={setShowDocDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Tambah Dokumen</DialogTitle></DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div><Label>Nama Dokumen *</Label><Input value={docForm.nama} onChange={(e) => setDocForm({...docForm, nama: e.target.value})} placeholder="Kontrak Kerja" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Kategori</Label>
                <Select value={docForm.kategori} onValueChange={(v) => setDocForm({...docForm, kategori: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="kontrak">Kontrak</SelectItem>
                    <SelectItem value="sertifikat">Sertifikat</SelectItem>
                    <SelectItem value="izin">Izin</SelectItem>
                    <SelectItem value="kebijakan">Kebijakan</SelectItem>
                    <SelectItem value="lainnya">Lainnya</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Karyawan</Label>
                <Select value={docForm.employee_id} onValueChange={(v) => setDocForm({...docForm, employee_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Perusahaan" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="company">Perusahaan (Umum)</SelectItem>
                    {employees.map((e) => (<SelectItem key={e.id} value={e.id}>{e.nama_lengkap}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>URL File *</Label><Input value={docForm.file_url} onChange={(e) => setDocForm({...docForm, file_url: e.target.value})} placeholder="https://drive.google.com/..." /></div>
            <div><Label>Deskripsi</Label><Textarea value={docForm.deskripsi} onChange={(e) => setDocForm({...docForm, deskripsi: e.target.value})} placeholder="Deskripsi dokumen" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Tanggal Berlaku</Label><Input type="date" value={docForm.tanggal_berlaku} onChange={(e) => setDocForm({...docForm, tanggal_berlaku: e.target.value})} /></div>
              <div><Label>Tanggal Kadaluarsa</Label><Input type="date" value={docForm.tanggal_kadaluarsa} onChange={(e) => setDocForm({...docForm, tanggal_kadaluarsa: e.target.value})} /></div>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox checked={docForm.is_confidential} onCheckedChange={(v) => setDocForm({...docForm, is_confidential: v})} />
              <Label>Dokumen Rahasia (Confidential)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDocDialog(false)}>Batal</Button>
            <Button onClick={handleCreateDocument} disabled={loading}>{loading ? "Menyimpan..." : "Simpan"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Policy Dialog */}
      <Dialog open={showPolicyDialog} onOpenChange={setShowPolicyDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Tambah Kebijakan</DialogTitle></DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div><Label>Judul *</Label><Input value={policyForm.judul} onChange={(e) => setPolicyForm({...policyForm, judul: e.target.value})} placeholder="Kebijakan Cuti Tahunan" /></div>
            <div className="grid grid-cols-3 gap-4">
              <div><Label>Kategori</Label>
                <Select value={policyForm.kategori} onValueChange={(v) => setPolicyForm({...policyForm, kategori: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hr">HR</SelectItem>
                    <SelectItem value="keuangan">Keuangan</SelectItem>
                    <SelectItem value="operasional">Operasional</SelectItem>
                    <SelectItem value="keamanan">Keamanan</SelectItem>
                    <SelectItem value="umum">Umum</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Versi</Label><Input value={policyForm.versi} onChange={(e) => setPolicyForm({...policyForm, versi: e.target.value})} placeholder="1.0" /></div>
              <div><Label>Berlaku Sejak</Label><Input type="date" value={policyForm.berlaku_sejak} onChange={(e) => setPolicyForm({...policyForm, berlaku_sejak: e.target.value})} /></div>
            </div>
            <div><Label>Konten *</Label><Textarea value={policyForm.konten} onChange={(e) => setPolicyForm({...policyForm, konten: e.target.value})} placeholder="Isi kebijakan..." rows={10} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPolicyDialog(false)}>Batal</Button>
            <Button onClick={handleCreatePolicy} disabled={loading}>{loading ? "Menyimpan..." : "Simpan"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Policy Detail Dialog */}
      <Dialog open={showPolicyDetail} onOpenChange={setShowPolicyDetail}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedPolicy?.judul}</DialogTitle>
          </DialogHeader>
          {selectedPolicy && (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              <div className="flex gap-2">
                {getKategoriBadge(selectedPolicy.kategori)}
                {getPolicyStatusBadge(selectedPolicy.status)}
                <Badge variant="outline">v{selectedPolicy.versi}</Badge>
              </div>
              <div className="prose prose-sm max-w-none">
                <pre className="whitespace-pre-wrap font-sans text-sm">{selectedPolicy.konten}</pre>
              </div>
              <div className="text-sm text-slate-500">
                Berlaku sejak: {selectedPolicy.berlaku_sejak}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPolicyDetail(false)}>Tutup</Button>
            {selectedPolicy?.status === "published" && (
              <Button onClick={() => { handleAcknowledgePolicy(selectedPolicy.id); setShowPolicyDetail(false); }}>
                <CheckCircle className="w-4 h-4 mr-2" />Acknowledge
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DocumentsPage;
